import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { authAPI, analyticsAPI } from '../api/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4', '#f97316'];

function formatMinutes(mins) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export default function AnalyticsPage() {
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [summary, setSummary] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authAPI.children().then(res => {
      setChildren(res.data.children);
      if (res.data.children[0]) setSelectedChild(res.data.children[0]);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (selectedChild) loadAnalytics(selectedChild._id);
  }, [selectedChild]);

  const loadAnalytics = async (childId) => {
    try {
      const res = await analyticsAPI.getSummary(childId);
      setSummary(res.data);
      // Build chart data from dailyData
      const data = (res.data.dailyData || []).map(d => ({
        date: format(new Date(d.date), 'EEE'),
        screenTime: Math.round((d.totalScreenTime || 0) / 60 * 10) / 10,
        unlocks: d.unlockCount || 0
      })).reverse();
      setDailyData(data);
    } catch { toast.error('Failed to load analytics'); }
  };

  if (loading) return <div className="spinner" />;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700 }}>📊 Usage Analytics</h2>
      </div>

      {/* Child Selector */}
      <div className="child-selector">
        {children.map(child => (
          <div key={child._id}
            className={`child-chip ${selectedChild?._id === child._id ? 'active' : ''}`}
            onClick={() => setSelectedChild(child)}
          >
            <div className="child-avatar">{child.name[0]}</div>
            {child.name}
          </div>
        ))}
      </div>

      {!summary ? (
        <div className="card"><div className="empty-state"><div className="empty-state-icon">📊</div><h3>No Data Yet</h3><p>Analytics will appear once the child device starts sending usage data.</p></div></div>
      ) : (
        <>
          {/* Summary Stats */}
          <div className="stats-grid" style={{ marginBottom: 20 }}>
            <div className="stat-card">
              <div className="stat-icon blue">⏱️</div>
              <div className="stat-info">
                <div className="stat-value">{formatMinutes(summary.avgScreenTime || 0)}</div>
                <div className="stat-label">Daily Avg Screen Time</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon orange">📱</div>
              <div className="stat-info">
                <div className="stat-value">{formatMinutes(summary.totalScreenTime || 0)}</div>
                <div className="stat-label">Total (7 Days)</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon purple">🔓</div>
              <div className="stat-info">
                <div className="stat-value">{summary.topApps?.length || 0}</div>
                <div className="stat-label">Apps Used</div>
              </div>
            </div>
          </div>

          <div className="dashboard-grid">
            {/* Daily Screen Time Bar Chart */}
            <div className="card">
              <div className="card-header">
                <div className="card-title">📅 Daily Screen Time (Hours)</div>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={dailyData}>
                  <XAxis dataKey="date" tick={{ fill: 'var(--text-secondary)', fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)' }}
                    formatter={(v) => [`${v}h`, 'Screen Time']}
                  />
                  <Bar dataKey="screenTime" fill="#3b82f6" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Top Apps Pie Chart */}
            <div className="card">
              <div className="card-header">
                <div className="card-title">📱 Top Apps (7 Days)</div>
              </div>
              {summary.topApps?.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={summary.topApps?.slice(0, 6)} dataKey="usageTime" nameKey="appName"
                        cx="50%" cy="50%" outerRadius={70} innerRadius={40}>
                        {summary.topApps?.slice(0, 6).map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => [formatMinutes(v), 'Usage']}
                        contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8 }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{ marginTop: 8 }}>
                    {summary.topApps?.slice(0, 5).map((app, i) => (
                      <div key={i} className="app-usage-item">
                        <div className="app-icon">📱</div>
                        <div className="app-info">
                          <div className="app-name">{app.appName || app.packageName}</div>
                          <div className="app-time">{formatMinutes(app.usageTime)}</div>
                        </div>
                        <div className="app-bar-container">
                          <div className="progress-bar">
                            <div className="progress-fill" style={{
                              width: `${Math.min(100, (app.usageTime / (summary.topApps[0]?.usageTime || 1)) * 100)}%`,
                              background: COLORS[i % COLORS.length]
                            }} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="empty-state" style={{ padding: 24 }}><p>No app data yet</p></div>
              )}
            </div>

            {/* Unlock count trend */}
            {dailyData.length > 0 && (
              <div className="card full-width">
                <div className="card-header">
                  <div className="card-title">🔓 Device Unlocks Per Day</div>
                </div>
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={dailyData}>
                    <XAxis dataKey="date" tick={{ fill: 'var(--text-secondary)', fontSize: 12 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)' }} />
                    <Line type="monotone" dataKey="unlocks" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981' }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
